/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import './PullToRefresh.css';

const PullToRefresh = ({ onRefresh, children, disabled = false }) => {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const pullThreshold = 80; // Порог для активации обновления (в пикселях)

  const findScrollableAncestor = (target) => {
    let element = target instanceof Element ? target : null;
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      const canScroll = /(auto|scroll)/.test(style.overflowY)
        && element.scrollHeight > element.clientHeight + 1;
      if (canScroll) return element;
      element = element.parentElement;
    }
    return null;
  };

  const canStartPull = (target) => {
    if (target instanceof Element && target.closest('.task-form-modal')) return false;
    const scroller = findScrollableAncestor(target);
    if (scroller) return scroller.scrollTop <= 0;
    return (window.pageYOffset || document.documentElement.scrollTop) <= 0;
  };

  // Определяем, является ли устройство мобильным
  useEffect(() => {
    const checkIsMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 ||
                            (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) ||
                            window.location.protocol === 'capacitor:' ||
                            window.location.protocol === 'file:' ||
                            'ontouchstart' in window;
      setIsMobile(isMobileDevice);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Обработка начала касания
  const handleTouchStart = (e) => {
    if (disabled || isRefreshing) return;

    if (!canStartPull(e.target)) return;

    const touch = e.touches[0];
    setStartY(touch.clientY);
    setIsPulling(true);
  };

  // Обработка движения касания
  const handleTouchMove = (e) => {
    if (disabled || isRefreshing || !isPulling) return;

    const touch = e.touches[0];
    const currentY = touch.clientY;
    const distance = currentY - startY;

    // Разрешаем только движение вниз
    if (distance > 0) {
      if (canStartPull(e.target)) {
        // Ограничиваем максимальное расстояние
        const maxDistance = pullThreshold * 2;
        const limitedDistance = Math.min(distance, maxDistance);
        setPullDistance(limitedDistance);

        // Предотвращаем прокрутку страницы при активном pull-to-refresh
        if (limitedDistance > 10) {
          e.preventDefault();
        }
      } else {
        setIsPulling(false);
        setPullDistance(0);
      }
    }
  };

  // Обработка окончания касания
  const handleTouchEnd = async () => {
    if (disabled || isRefreshing || !isPulling) return;

    setIsPulling(false);

    // Если потянули достаточно далеко, запускаем обновление
    if (pullDistance >= pullThreshold) {
      setIsRefreshing(true);
      setPullDistance(pullThreshold);

      try {
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        console.error('Error during refresh:', error);
      } finally {
        // Анимация завершения
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 300);
      }
    } else {
      // Если не потянули достаточно, возвращаем в исходное положение
      setPullDistance(0);
    }
  };

  // Добавляем обработчики событий
  // Handlers intentionally depend on the current pull state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (disabled || !isMobile) return;

    // Touch события на document для лучшего контроля
    const handleTouchStartWrapper = (e) => {
      if (canStartPull(e.target)) {
        handleTouchStart(e);
      }
    };

    const handleTouchMoveWrapper = (e) => {
      if (isPulling) {
        handleTouchMove(e);
      }
    };

    const handleTouchEndWrapper = () => {
      if (isPulling) {
        handleTouchEnd();
      }
    };

    document.addEventListener('touchstart', handleTouchStartWrapper, { passive: false });
    document.addEventListener('touchmove', handleTouchMoveWrapper, { passive: false });
    document.addEventListener('touchend', handleTouchEndWrapper, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStartWrapper);
      document.removeEventListener('touchmove', handleTouchMoveWrapper);
      document.removeEventListener('touchend', handleTouchEndWrapper);
    };
  }, [isPulling, pullDistance, isRefreshing, disabled, isMobile, startY]);

  // Вычисляем прогресс pull-to-refresh (0-1)
  const progress = Math.min(pullDistance / pullThreshold, 1);
  const canRefresh = pullDistance >= pullThreshold;

  return (
    <div
      ref={containerRef}
      className="pull-to-refresh-container"
      style={{
        transform: `translateY(${isRefreshing ? pullThreshold : pullDistance}px)`,
        transition: isPulling ? 'none' : 'transform 0.3s ease',
      }}
    >
      {/* Индикатор обновления */}
      <div
        className={`pull-to-refresh-indicator ${isRefreshing ? 'refreshing' : ''} ${canRefresh ? 'can-refresh' : ''}`}
        style={{
          opacity: isPulling || isRefreshing ? 1 : 0,
          transform: `scale(${progress})`,
        }}
      >
        <div className="pull-to-refresh-icon">
          {isRefreshing ? (
            <div className="spinner-small"></div>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{
                transform: `rotate(${canRefresh ? 180 : progress * 180}deg)`,
                transition: 'transform 0.3s ease',
              }}
            >
              <path d="M1 4v6h6M23 20v-6h-6" />
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
            </svg>
          )}
        </div>
        <div className="pull-to-refresh-text">
          {isRefreshing ? 'Обновление...' : canRefresh ? 'Отпустите для обновления' : 'Потяните для обновления'}
        </div>
      </div>

      {/* Контент */}
      <div className="pull-to-refresh-content">
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
